import { Fragment, useCallback, useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { Button, Grid } from '@mui/material';

import { MasterIndexApi, MasterIndexApiPath, MasterVehicleApi } from '@/api';
import { MasterVehicleApi as MasterVehicleNotesApi } from '@/api/api.vehicles.master';
import {
  FormRoot,
  FormCard,
  ViewItem,
  IViewCard,
  FileGrid,
  PageHeader,
} from '@/components';
import NavigationButtonGroup from '@/components/NavigationButtonGroup/NavigationButtonGroup';
import { USStates } from '@/constants';
import { useFormConfigLoader, useKaseContext } from '@/hooks';
import { TabAnimationWrapper, useTabLayoutContext } from '@/layouts/TabLayout';
import { IMasterName, IMasterVehicle, IOffenseOptionItem } from '@/models';
import { getOffenseOptionItems } from '@/pages/Kases/KaseCrimes/utilities';
import { MasterIndexDetailsContext } from '@/pages/MasterIndex/components/MasterIndexDetailsPage';
import MasterIndexRelatedRecords from '@/pages/MasterIndex/components/MasterIndexRelatedRecords/MasterIndexRelatedRecords';
import MasterIndexVehicleNotes from '@/pages/MasterIndex/Vehicles/MasterIndexVehiclesNotes';
import { useToastStore } from '@/store';
import {
  deflatten,
  flattenObject,
  formatPhoneNumber,
  getMasterNameDisplayName,
  getMasterNamePhoneNumbersByPriority,
  getMasterNamePrimaryAddressText,
} from '@/utils';

import { useMasterVehicleContext } from '..';
import { MasterVehicleScreens, MasterVehicleViewFields } from '../data';
import {
  buildMasterVehicleTitle,
  getNormalizedLicensePlates,
  sortActiveLicensePlateFirst,
} from '../licensePlate.utils';

interface TabCounts {
  relatedRecords: number;
  notes: number;
}

export const renderViewCardContent = (
  { key, items }: IViewCard,
  flattenData: Record<string, any>,
  PropertyType?: Map<string, IOffenseOptionItem>,
) => {
  const fieldsWithDifferentSizes = ['vehicleInformation', 'vehicleDescription'];
  const mdValue = fieldsWithDifferentSizes.includes(key) ? 4 : 2;

  if (key === 'photos') {
    return (
      <ViewItem sx={{ mt: -4 }} md={8} key={key}>
        <FileGrid files={flattenData.files} />
      </ViewItem>
    );
  }

  if (key === 'ownerinfo') {
    const owner = flattenData['owner._id']
      ? (deflatten(flattenData, 'owner') as IMasterName)
      : null;

    if (!owner) return null;

    const fullName = getMasterNameDisplayName(owner);
    const { primaryPhoneNumber } = getMasterNamePhoneNumbersByPriority(owner);
    const addressText = getMasterNamePrimaryAddressText(owner);

    const type = owner.type ?? '';

    return (
      <>
        <ViewItem label="Name" value={fullName} copyable md={3} />
        <ViewItem
          label="Phone Number"
          value={formatPhoneNumber(primaryPhoneNumber?.phoneNumber ?? '')}
          copyable
          md={3}
        />
        <ViewItem
          label="Address"
          value={addressText}
          showMapsIcon={true}
          copyable
          md={4}
        />
        <ViewItem label="Type" value={type} copyable md={2} />
      </>
    );
  }

  if (key === 'verificationStatus') {
    return (
      <ViewItem
        md={2}
        label={items[0].label}
        value={flattenData?.isVerified ? 'Verified' : 'Not Verified'}
        copyable
        sx={{ fontWeight: 500 }}
      />
    );
  }

  return items.map((item) => {
    let value = flattenData[item.value as string];
    if (item.value === 'licensePlate.state') {
      value = USStates.find(
        ({ value: newValue }) => newValue === flattenData[item.value as string],
      )?.label;
    } else if (item.value === 'type' && PropertyType) {
      const allValues = PropertyType?.values();
      value = PropertyType.size
        ? [...allValues].find((propItem) => propItem.key === value)?.label
        : '';
    }
    return (
      <ViewItem
        valueAsTooltip
        key={item.label}
        {...item}
        value={value}
        md={mdValue}
        copyable
      />
    );
  });
};

interface MasterVehicleViewProps {
  isMasterIndex?: boolean;
  hideHeader?: boolean;
  onGoBack?: () => void;
}

export const MasterVehicleView = ({
  isMasterIndex,
  hideHeader,
  ...props
}: MasterVehicleViewProps) => {
  const outletContext = useOutletContext<
    MasterIndexDetailsContext | undefined
  >();
  const setCustomTitle = outletContext?.setCustomTitle;
  const setLastUpdatedAt = outletContext?.setLastUpdatedAt;
  const { updateToast } = useToastStore();
  const { updateCurrentScreen } = useTabLayoutContext();
  const { currentVehicleId } = useMasterVehicleContext();
  const { isReadOnlyVehicles } = useKaseContext();
  const [currentVehicle, setCurrentVehicle] = useState<IMasterVehicle>();
  const [activeTab, setActiveTab] = useState('information');
  const [tabCounts, setTabCounts] = useState<TabCounts>({
    relatedRecords: 0,
    notes: 0,
  });
  const flattenVehicleObj = flattenObject(currentVehicle);

  const { vehicleId } = useParams();
  const { filteredOptions } = useFormConfigLoader({
    optionsName: 'PROPERTY',
    category: ['PROPERTY'],
  });
  const PropertyType = getOffenseOptionItems(
    'propertyType',
    filteredOptions,
  ) as Map<string, IOffenseOptionItem>;

  const fetchTabCounts = useCallback(async (id: string) => {
    if (!id) return;

    try {
      const [relatedRes, notesRes] = await Promise.all([
        MasterIndexApi.getMasterRelatedRecords(
          MasterIndexApiPath.Vehicles,
          id,
          { sort: JSON.stringify({ createdAt: -1 }) },
        ),
        MasterVehicleNotesApi.getPaginatedMasterVehicleNotes(id, { limit: 1 }),
      ]);

      setTabCounts({
        relatedRecords:
          relatedRes.data?.totalDocs || relatedRes.data?.docs?.length || 0,
        notes: notesRes.data?.totalDocs || 0,
      });
    } catch {
      // Silently fail - counts are non-critical
    }
  }, []);

  useEffect(() => {
    const id = currentVehicleId || vehicleId;
    if (id) {
      fetchCurrentVehicle();
      fetchTabCounts(String(id));
    }
  }, [currentVehicleId, vehicleId]);

  const fetchCurrentVehicle = async () => {
    try {
      const api = isMasterIndex ? MasterIndexApi : MasterVehicleApi;
      const res = await api.getMasterVehicle(
        isMasterIndex ? (vehicleId as string) : String(currentVehicleId),
      );
      setCurrentVehicle(res.data);

      if (res.data.uniqueId && setCustomTitle) {
        const customTitle = buildMasterVehicleTitle(res.data);
        setCustomTitle(customTitle);
      }
      setLastUpdatedAt?.(res.data?.updatedAt);
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
  };

  const onGoBack = () => {
    if (props.onGoBack) {
      return props.onGoBack();
    }
    updateCurrentScreen(MasterVehicleScreens.MasterVehicleList);
  };

  const handleEdit = () => {
    if (currentVehicle) {
      updateCurrentScreen(MasterVehicleScreens.MasterVehicleForm);
    }
  };

  const getTabs = () => {
    return [
      {
        label: 'Vehicle information',
        path: 'information',
      },
      {
        label: 'Related records',
        path: 'related',
        count: tabCounts.relatedRecords,
      },
      {
        label: 'Notes',
        path: 'notes',
        count: tabCounts.notes,
      },
    ];
  };

  const normalizedLicensePlates = sortActiveLicensePlateFirst(
    getNormalizedLicensePlates(currentVehicle),
  );

  const tabs = getTabs();

  const renderTabContent = () => {
    switch (activeTab) {
      case 'information':
        return (
          <FormRoot
            sx={{
              mt: 1,
              '.p-kase-edit &': { p: 2.5, borderRadius: 2 },
            }}
          >
            {MasterVehicleViewFields.map((data) => {
              if (data.key === 'vehicleInformation') {
                const plateRows =
                  normalizedLicensePlates.length > 0
                    ? normalizedLicensePlates
                    : [{ number: '', state: '', year: '', type: '' }];

                return (
                  <FormCard
                    className="grey-title"
                    title={data.title}
                    key={data.key}
                  >
                    <ViewItem
                      label="VIN number"
                      value={currentVehicle?.vin || ''}
                      md={3}
                      copyable
                    />
                    <Grid item xs={12} />
                    {plateRows.map((plate, index) => {
                      const stateLabel =
                        USStates.find(({ value }) => value === plate.state)
                          ?.label ||
                        plate.state ||
                        '';

                      return (
                        <Fragment key={`${plate.number || 'empty'}-${index}`}>
                          <ViewItem
                            label="License plate number"
                            value={plate.number || ''}
                            md={3}
                            copyable
                          />
                          <ViewItem
                            label="License plate state"
                            value={stateLabel}
                            md={3}
                            copyable
                          />
                          <ViewItem
                            label="License plate year"
                            value={plate.year || ''}
                            md={3}
                            copyable
                          />
                          <ViewItem
                            label="License plate type"
                            value={plate.type || ''}
                            md={3}
                            copyable
                          />
                        </Fragment>
                      );
                    })}
                  </FormCard>
                );
              }

              return (
                <FormCard
                  className="grey-title"
                  title={data.title}
                  key={data.key}
                >
                  {renderViewCardContent(data, flattenVehicleObj, PropertyType)}
                </FormCard>
              );
            })}
          </FormRoot>
        );

      case 'related':
        return (
          <MasterIndexRelatedRecords
            type={MasterIndexApiPath.Vehicles}
            viewId={currentVehicleId || vehicleId}
          />
        );

      case 'notes':
        return (
          <MasterIndexVehicleNotes
            viewId={currentVehicleId || vehicleId}
            onCountChange={() =>
              fetchTabCounts(String(currentVehicleId || vehicleId || ''))
            }
          />
        );

      default:
        return null;
    }
  };

  const tabsWithHandlers = tabs.map((tab) => ({
    ...tab,
    onClick: () => setActiveTab(tab.path),
  }));

  const getVehicleTitle = () => {
    return buildMasterVehicleTitle(currentVehicle) || undefined;
  };

  const renderActionButtons = () => {
    if (activeTab !== 'information') {
      return null;
    }

    if (!isReadOnlyVehicles) {
      return (
        <Button color="error" variant="contained" onClick={handleEdit}>
          Edit
        </Button>
      );
    }
  };

  return (
    <TabAnimationWrapper>
      <div className="bg-[#F5F7F7] dark:bg-[#0B1922] flex flex-col h-full overflow-hidden">
        {!hideHeader && (
          <div className="p-2 flex-shrink-0">
            <PageHeader
              title={getVehicleTitle()}
              onBack={onGoBack}
              action={!isMasterIndex ? renderActionButtons() : undefined}
            />
          </div>
        )}

        <div className="!w-full px-4">
          {!hideHeader && tabs && (
            <NavigationButtonGroup
              tabs={tabsWithHandlers}
              activeTab={activeTab}
            />
          )}
        </div>

        <div className="flex-1 overflow-auto px-4 mb-4 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          {renderTabContent()}
        </div>
      </div>
    </TabAnimationWrapper>
  );
};
